import type { ScopeFile } from "services/fileApi/types/ScopeFile";
import splitFilename from "./splitFilename";

/** The orders the file explorer's sort selector offers, each ascending. */
export const FILE_SORT_KEYS = ["date", "name", "extension"] as const;
export type FileSortKey = (typeof FILE_SORT_KEYS)[number];

// Ignores case and accents, and reads runs of digits as numbers, so
// `Report.pdf` sorts beside `report.pdf` and `plot2.png` before `plot10.png`.
const filenameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

/**
 * Returns the files in a new array, in `sortKey`'s order (ADR 071):
 *
 * - `date`: oldest first.
 * - `name`: by base name, then by extension.
 * - `extension`: by extension, then by base name. A file with no extension
 *   sorts first.
 *
 * Files with the same base name and extension keep their date order.
 */
export default function sortFiles(
  files: ScopeFile[],
  sortKey: FileSortKey
): ScopeFile[] {
  const byDate = files.toSorted(
    (a, b) => a.createdAt.toMillis() - b.createdAt.toMillis()
  );
  if (sortKey === "date") return byDate;

  // `toSorted` is stable, so files this comparison ranks equal stay in date
  // order.
  return byDate.toSorted((a, b) => {
    const nameA = splitFilename(a.filename);
    const nameB = splitFilename(b.filename);
    const baseNameOrder = filenameCollator.compare(
      nameA.baseName,
      nameB.baseName
    );
    // The empty string sorts before any extension.
    const extensionOrder = filenameCollator.compare(
      nameA.extension ?? "",
      nameB.extension ?? ""
    );
    if (sortKey === "name") {
      return baseNameOrder !== 0 ? baseNameOrder : extensionOrder;
    }
    return extensionOrder !== 0 ? extensionOrder : baseNameOrder;
  });
}
